// The URL a signed-out visitor opened the web app at — typically a
// /mix/<dj>/<title> link from a mixgoat.com preview ("+N more tracks in
// MixGoat") — kept until the first signed-in render can apply it.
//
// It has to be captured at load and stored, because by the time the visitor
// has signed in the address bar says '/': App.js's tab→URL effect rewrites it
// on the first render, and Google sign-in leaves the page entirely
// (auth/google/callback.html returns to '/'). sessionStorage survives that
// round trip in the same tab; the in-memory copy covers browsers that refuse
// storage. A stale entry (the visitor wandered off and signed in much later)
// is dropped rather than applied.

const KEY = 'mixgoat:pendingWebDeepLink';
const MAX_AGE_MS = 30 * 60 * 1000;

let memo = null;

function isDeepLinkPath(pathname) {
    return typeof pathname === 'string'
        && pathname !== '/'
        && pathname !== ''
        && !pathname.startsWith('/auth/')
        && !pathname.startsWith('/index.html');
}

/** Record the URL the app was opened at, if it points past the home page. */
export function captureOpenedWebUrl(location = window.location, now = Date.now()) {
    if (!location || !isDeepLinkPath(location.pathname)) return;
    const entry = { url: `${location.pathname}${location.search || ''}`, at: now };
    memo = entry;
    try { window.sessionStorage.setItem(KEY, JSON.stringify(entry)); } catch { /* storage refused: memo covers it */ }
}

/**
 * Hand over the recorded URL once and forget it. Returns { pathname, search }
 * or null when there is none or it is older than MAX_AGE_MS.
 */
export function takeOpenedWebUrl(now = Date.now()) {
    let entry = memo;
    memo = null;
    try {
        const raw = window.sessionStorage.getItem(KEY);
        window.sessionStorage.removeItem(KEY);
        if (raw) entry = JSON.parse(raw);
    } catch { /* storage refused or bad JSON: fall back to memo */ }
    if (!entry || typeof entry.url !== 'string' || !(now - entry.at <= MAX_AGE_MS)) return null;
    const q = entry.url.indexOf('?');
    const pathname = q === -1 ? entry.url : entry.url.slice(0, q);
    if (!isDeepLinkPath(pathname)) return null;
    return { pathname, search: q === -1 ? '' : entry.url.slice(q) };
}
